import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";

export default function SetPassword() {
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | invalid | done
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api.get(`/auth/invitation/${token}`)
      .then((r) => { setInfo(r.data); setState("ready"); })
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/accept-invitation", { token, password });
      setState("done");
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not set password");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8" data-testid="set-password-card">
        <div className="flex justify-center mb-6"><Logo /></div>
        {state === "loading" && <div className="flex justify-center py-8"><Loader2 className="animate-spin text-brand" /></div>}
        {state === "invalid" && (
          <div className="text-center space-y-3" data-testid="invite-invalid">
            <h1 className="text-xl font-display font-bold text-slate-800">Invitation invalid</h1>
            <p className="text-sm text-slate-500">This set-password link is invalid or has expired. Please ask your CaterCTRL administrator to send a new invitation.</p>
            <Button variant="outline" onClick={() => navigate("/login")}>Back to sign in</Button>
          </div>
        )}
        {state === "done" && (
          <div className="text-center space-y-3" data-testid="invite-done-screen">
            <CheckCircle2 className="mx-auto text-brand" size={44} />
            <h1 className="text-xl font-display font-bold text-slate-800">Password set</h1>
            <p className="text-sm text-slate-500">Your account is ready. Redirecting you to sign in…</p>
          </div>
        )}
        {state === "ready" && (
          <form onSubmit={submit} className="space-y-4">
            <div className="text-center">
              <h1 className="text-xl font-display font-bold text-slate-800">Set your password</h1>
              <p className="text-sm text-slate-500 mt-1">Welcome{info?.name ? `, ${info.name}` : ""}{info?.org_name ? ` · ${info.org_name}` : ""}</p>
              {info?.email && <p className="text-xs text-slate-400 mt-1">{info.email}</p>}
            </div>
            <div><Label>New password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="set-password-input" className="mt-1.5" placeholder="At least 8 characters" /></div>
            <div><Label>Confirm password</Label><Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="set-password-confirm" className="mt-1.5" /></div>
            <Button type="submit" disabled={busy} data-testid="set-password-submit" className="w-full bg-brand hover:bg-brand-hover">{busy ? <Loader2 className="animate-spin" size={16} /> : "Set password & continue"}</Button>
          </form>
        )}
      </div>
    </div>
  );
}
